import { useTranslations } from '@vocab/react';
import {
  IconNewWindow,
  IconEdit,
  IconCopy,
  IconDelete,
  IconDownload,
  IconMail,
  IconPersonAdd,
  IconInfo,
  IconSocialFacebook,
  IconSocialX,
  IconSocialLinkedIn,
  IconLink,
  IconRefresh,
} from 'braid-design-system';
import type React from 'react';

import type { IJob } from '../../../../stores/JobStore/Job';
import translations from '../.vocab';
import { JobAction } from '../actions';

import useJobActionStatus from './useJobActionStatus';

interface Option {
  value: JobAction;
  label: string;
  disabled: boolean;
  icon?: React.ReactNode;
}

type Options = [
  jobActionOptions: Option[],
  jobShareActionOptions: Option[],
  jobConfigOptions: Option[],
];

export const useJobActionOptions = ({
  job,
  showExportToCsv,
  isIndirectJobButCanBeChanged,
}: {
  job: IJob;
  showExportToCsv: boolean;
  isIndirectJobButCanBeChanged: boolean;
}): Options => {
  const { t } = useTranslations(translations);

  const jobActionStatus = useJobActionStatus(job, isIndirectJobButCanBeChanged);

  const jobActionOptions = [
    {
      value: JobAction.VIEW,
      label: t('View job'),
      ...jobActionStatus.viewActionStatus,
      icon: <IconNewWindow />,
    },
    {
      value: JobAction.VIEW_AD_INFORMATION,
      label: t('View ad information'),
      disabled: false,
      icon: <IconInfo />,
    },
    {
      value: JobAction.EDIT,
      label: t('Edit job'),
      ...jobActionStatus.editActionStatus,
      icon: <IconEdit />,
    },
    {
      value: JobAction.REFRESH,
      label: t('Refresh job'),
      ...jobActionStatus.refreshActionStatus,
      icon: <IconRefresh />,
    },
    {
      value: JobAction.COPY,
      label: t('Copy job'),
      ...jobActionStatus.copyActionStatus,
      icon: <IconCopy />,
    },
    {
      value: JobAction.EXPIRE,
      label: t('Expire job'),
      ...jobActionStatus.expireActionStatus,
      icon: <IconDelete />,
    },
  ].flatMap((options) => options);

  const shareJobActionOptions = [
    {
      value: JobAction.SHARE_ON_FACEBOOK,
      label: t('Share job on {socialMedia}', {
        socialMedia: 'Facebook',
      }),
      ...jobActionStatus.shareActionStatus,
      icon: <IconSocialFacebook />,
    },
    {
      value: JobAction.SHARE_ON_X,
      label: t('Share job on {socialMedia}', {
        socialMedia: 'X',
      }),
      ...jobActionStatus.shareActionStatus,
      icon: <IconSocialX />,
    },
    {
      value: JobAction.SHARE_ON_LINKEDIN,
      label: t('Share job on {socialMedia}', {
        socialMedia: 'LinkedIn',
      }),
      ...jobActionStatus.shareActionStatus,
      icon: <IconSocialLinkedIn />,
    },
    {
      value: JobAction.COPY_LINK_TO_JOB,
      label: t('Copy link to job'),
      ...jobActionStatus.shareActionStatus,
      icon: <IconLink />,
    },
  ].flatMap((options) => options);

  const jobConfigOptions = [
    showExportToCsv
      ? [
          {
            value: JobAction.EXPORT_CANDIDATE_LIST,
            label: t('Export candidate list'),
            disabled: false,
            icon: <IconDownload />,
          },
        ]
      : [],
    {
      value: JobAction.NOTIFICATION_PREFERENCE,
      label: t('Notifications'),
      ...jobActionStatus.notificationActionStatus,
      icon: <IconMail />,
    },
    {
      value: JobAction.TEAM_MEMBER_ACCESS,
      label: t('Team access'),
      ...jobActionStatus.teamAccessActionStatus,
      icon: <IconPersonAdd />,
    },
  ].flatMap((options) => options);

  return [jobActionOptions, shareJobActionOptions, jobConfigOptions];
};
